import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser, LoginPayload, LoginResponse, AuthenticatedUser } from "../api";
import './LoginView.css';

interface LoginViewProps {
  onLoginSuccess: (user: AuthenticatedUser, token: string) => void;
}

const LoginView: React.FC<LoginViewProps> = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const errorTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showError = (msg: string) => {
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    setError(msg);
    errorTimerRef.current = setTimeout(() => setError(""), 3500);
  };

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    if (errorTimerRef.current) clearTimeout(errorTimerRef.current);
    setError("");
    setIsLoading(true);

    if (!email.trim()) {
      showError("Please enter your email.");
      setIsLoading(false);
      return;
    }

    const payload: LoginPayload = { email: email.trim(), password };

    try {
      const response: LoginResponse = await loginUser(payload);

      if (response.success && response.user) {
        if (response.user.is_first_login) {
          navigate('/change-password', {
            state: { email: response.user.email }
          });
        } else {
          const token = (response.token || '').trim();
          if (!token) {
            showError('Login succeeded but no auth token was returned. Please contact support.');
            return;
          }
          onLoginSuccess(response.user, token);
        }
      } else {
        showError(response.message || "Login failed. Please check your credentials.");
      }
    } catch (err: any) {
      console.error("Login error:", err);
      if (err.response?.data?.message) {
        showError(err.response.data.message);
      } else {
        showError("An unexpected error occurred during login. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <img src="/daikin-logo.png" alt="Daikin" className="login-logo" />
          <h1 className="login-brand-name">QFT</h1>
          <p className="login-brand-sub">Quarterly Finance Tool</p>
        </div>

        <form className="login-form" onSubmit={handleLogin}>
          <div className="login-field">
            <label htmlFor="emailInput" className="login-label">Email</label>
            <input
              id="emailInput"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              className="login-input"
              placeholder="your.name@company.com"
              autoComplete="email"
              autoFocus
            />
          </div>

          <div className="login-field">
            <label htmlFor="passwordInput" className="login-label">Password</label>
            <div className="login-pwd-wrap">
              <input
                id="passwordInput"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                className="login-input"
                autoComplete="current-password"
              />
              <button
                type="button"
                className="login-pwd-eye"
                onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
              >
                <span className="material-icons">{showPassword ? "visibility_off" : "visibility"}</span>
              </button>
            </div>
          </div>

          {error && (
            <div className="login-error">
              <span className="material-icons">error_outline</span>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading || !email.trim()}
            className="login-submit"
          >
            {isLoading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="login-footer">
          <button
            type="button"
            className="login-forgot-btn"
            onClick={() => setShowForgot(v => !v)}
          >
            {showForgot ? "Cancel" : "Forgot password?"}
          </button>

          {showForgot && (
            <div className="login-forgot-info">
              Contact your <strong>Finance Admin</strong> to reset your password.
              They will provide a temporary password — you'll be prompted to set a new one on login.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginView;
